import { createRouter, createWebHistory } from 'vue-router'
import ContentLayout from '@/layouts/ContentLayout.vue'
import config from '@/config'
import { ROUTE_NAMES, ROUTE_PATHS } from './routes'
import WorkspacesLayout from '@/layouts/WorkspacesLayout.vue'
import { loadMars3dRuntime } from '@/services/mars3d/loadRuntime'

const routes = [
  {
    path: '/',
    component: WorkspacesLayout,
    children: [
      {
        path: ROUTE_PATHS.DEFAULT,
        name: ROUTE_NAMES.HOME,
        component: () => import('@/views/WelcomeView.vue'),
      },
      {
        path: ROUTE_PATHS.WORKSPACES,
        name: ROUTE_NAMES.WORKSPACES,
        component: () => import('@/views/workspaces/WorkspacesPage.vue'),
      },
    ],
  },
  {
    path: '/workspaces',
    component: ContentLayout,
    children: [
      {
        path: ROUTE_PATHS.WORKSPACE_DETAIL,
        name: ROUTE_NAMES.WORKSPACE_DETAIL,
        // HomePage.vue always renders the 3D map (it stays mounted underneath
        // whichever tab is active), so Cesium/mars3d must be loaded before
        // its module graph evaluates.
        component: () => loadMars3dRuntime().then(() => import('@/views/HomePage.vue')),
      },
    ],
  },
  {
    // Development-only MVT provider smoke test. The Vite endpoint it uses is
    // enabled only with VITE_DEV_MVT_SMOKE=true; production has neither data
    // endpoint nor auth bypass.
    path: ROUTE_PATHS.MVT_VECTOR_SMOKE,
    name: ROUTE_NAMES.MVT_VECTOR_SMOKE,
    component: () => loadMars3dRuntime().then(() => import('@/views/dev/MvtVectorSmoke.vue')),
  },
  {
    // Development-only Select by Location highlight smoke test -- verifies
    // mars3d's openHighlight(style, closeLast=false) lets two graphics stay
    // independently highlighted and that closeHighlight() on one restores
    // only that graphic (no data endpoint or auth bypass needed).
    path: ROUTE_PATHS.SELECT_BY_LOCATION_SMOKE,
    name: ROUTE_NAMES.SELECT_BY_LOCATION_SMOKE,
    component: () => loadMars3dRuntime().then(() => import('@/views/dev/SelectByLocationSmoke.vue')),
  },
  {
    // Development-only vector symbology smoke test (Phase 1 of the vector
    // style editor) -- verifies curated materials render, the merge:false
    // restyle decision clears stale keys, and the curated polygon outline
    // config is reachable through the real Styles.vue form. No data
    // endpoint or auth bypass needed.
    path: ROUTE_PATHS.VECTOR_SYMBOLOGY_SMOKE,
    name: ROUTE_NAMES.VECTOR_SYMBOLOGY_SMOKE,
    component: () => loadMars3dRuntime().then(() => import('@/views/dev/VectorSymbologySmoke.vue')),
  },
  {
    // Pure-presentation harness for the redesigned Style dialog panels: no
    // mars3d runtime, no layer store, static symbols.
    path: ROUTE_PATHS.STYLE_DIALOG_SMOKE,
    name: ROUTE_NAMES.STYLE_DIALOG_SMOKE,
    component: () => import('@/views/dev/StyleDialogSmoke.vue'),
  },
  {
    // Isolated from the production AOI path by design (Flood 3D reference
    // parity investigation) — no mars3d/Cesium runtime, no map, no layout.
    path: ROUTE_PATHS.FLOOD_PARITY_BENCHMARK,
    name: ROUTE_NAMES.FLOOD_PARITY_BENCHMARK,
    component: () => import('@/views/dev/FloodParityBenchmark.vue'),
  },
  {
    // Physics-only validation suite — separate from the rendering parity
    // benchmark above, per the plan: tests raw depth-field correctness
    // (mass conservation, well-balanced lake-at-rest, wetting/drying
    // stability) independent of any visual question.
    path: ROUTE_PATHS.FLOOD_PHYSICS_VALIDATION,
    name: ROUTE_NAMES.FLOOD_PHYSICS_VALIDATION,
    component: () => import('@/views/dev/FloodPhysicsValidation.vue'),
  },
  {
    // Phase 1A gate for the texture-centric architecture refactor: proves
    // whether canonical fp32 depth textures support exact textureLoad reads
    // (physics) and render-side sampling, and whether the ping-pong texture
    // can be re-bound without rebuilding the material. Disposable — delete
    // once Phase 1B lands.
    path: ROUTE_PATHS.FLOOD_TEXTURE_SMOKE_TEST,
    name: ROUTE_NAMES.FLOOD_TEXTURE_SMOKE_TEST,
    component: () => import('@/views/dev/FloodTextureSmokeTest.vue'),
  },
  {
    // Phase V2-A design gates for the Conservative Two-Pass solver: GPU
    // arithmetic parity, RGBA32Float storage viability, and whether the CPU
    // replay is a bit-exact oracle. Disposable once V2-A closes.
    path: ROUTE_PATHS.FLOOD_V2_PROBES,
    name: ROUTE_NAMES.FLOOD_V2_PROBES,
    component: () => import('@/views/dev/FloodV2Probes.vue'),
  },
  {
    // Phase V2-E performance baseline: V1 vs V2 solver cost, isolated from
    // the numerical probes above so DOM/reactive updates never pollute a
    // timed region. Disposable once V2-E closes.
    path: ROUTE_PATHS.FLOOD_V2_PERFORMANCE,
    name: ROUTE_NAMES.FLOOD_V2_PERFORMANCE,
    component: () => import('@/views/dev/FloodV2Performance.vue'),
  },
  {
    // Phase 3 analysis outputs (maxDepth / arrivalTime / duration / flux):
    // WebGPU binding-shape audit, seed/lifecycle gates, per-field correctness
    // against CPU references, and the final production-path smoke. Kept
    // separate from FloodV2Probes.vue, which is already large and answers a
    // different question (Phase-2 depth-solver design gates).
    path: ROUTE_PATHS.FLOOD_V2_ANALYSIS_PROBES,
    name: ROUTE_NAMES.FLOOD_V2_ANALYSIS_PROBES,
    component: () => import('@/views/dev/FloodV2AnalysisProbes.vue'),
  },
  {
    // Phase 4 productization/visualization mechanic gates: fragment-stage
    // nearest-cell textureLoad, offset single-texel readback, readback
    // resource lifetime, attribute() in a material, and GPU-matched terrain
    // interpolation for picking. Kept separate from FloodV2AnalysisProbes.vue,
    // which answers Phase-3's numerical-correctness question, not Phase-4's
    // rendering/UI mechanics.
    path: ROUTE_PATHS.FLOOD_ANALYSIS_VIZ_PROBES,
    name: ROUTE_NAMES.FLOOD_ANALYSIS_VIZ_PROBES,
    component: () => import('@/views/dev/FloodAnalysisVizProbes.vue'),
  },
  {
    // 4H closure smoke: mounts the REAL production components
    // (ControlFlood3DLauncher -> Flood3DView, and everything they render —
    // FloodAnalysisPanel, FloodDepthLegend, FloodAnalysisQueryPanel)
    // unmodified, through a real mars3d/Cesium map and the real terrain-tile
    // service. Only the Keycloak-gated login and the mouse-driven AOI draw
    // interaction are bypassed (a synthetic AOI is injected directly into
    // the flood-3d Pinia store) — everything downstream is production code,
    // driven by real DOM interactions. See Flood3DProductionSmoke.vue's own
    // header comment for why this differs from every other /dev/* route
    // here (which import bare TS classes, never a mounted production
    // component tree).
    path: ROUTE_PATHS.FLOOD_3D_PRODUCTION_SMOKE,
    name: ROUTE_NAMES.FLOOD_3D_PRODUCTION_SMOKE,
    component: () =>
      loadMars3dRuntime().then(() => import('@/views/dev/Flood3DProductionSmoke.vue')),
  },
  {
    // Phase 5 forensic evidence: a bounded, deterministic PerspectiveCamera
    // pose sweep proving the camera-angle-dependent analysis/glyph rendering
    // defect reproduces on real hardware (every prior Phase-4 gate used an
    // orthographic camera, structurally blind to this), plus a realistic-
    // scale flux-glyph detectability repro and an ArrowHelper WebGPU mechanic
    // check. No mars3d/Cesium runtime needed — same isolation as the other
    // bare-TS-class dev probes.
    path: ROUTE_PATHS.FLOOD_PHASE5_CAMERA_PROBES,
    name: ROUTE_NAMES.FLOOD_PHASE5_CAMERA_PROBES,
    component: () => import('@/views/dev/FloodPhase5CameraProbes.vue'),
  },
  {
    // Windy-style flux particle system: first use of instancedArray/GPU
    // storage-buffer compute in this codebase — a mechanic probe (does it
    // render, does textureLoad work inside .compute(), does dispose() free
    // GPU memory) verified here on real hardware BEFORE any production
    // integration, plus the spec's own required statistical/zoom/pan
    // acceptance tests.
    path: ROUTE_PATHS.FLOOD_FLUX_PARTICLE_PROBES,
    name: ROUTE_NAMES.FLOOD_FLUX_PARTICLE_PROBES,
    component: () => import('@/views/dev/FloodFluxParticleProbes.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: ROUTE_NAMES.NOT_FOUND,
    component: () => import('@/views/NotFound.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(config.baseUrl),
  routes,
})

export default router
